import React, { Suspense, useEffect, useState } from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getAuthUser, setAccess } from "../actions/initialData";
import Loader from "../components/common/Loader";
import { teacher } from "../puglins/Utils/models";
import noNetworkAction from "../puglins/Utils/errorManage";
import notistack from "../puglins/notistack";
import { t } from "i18next";
import { requestOnlinePrompt, tokenUserType } from "../helper/teacherPresence";
import { PageMeta } from "../helper/seo";
import { PAGE_LOADERS } from "./preload";
import { arriveWithPartnerCode, searchWithoutVia, viaCodeOf } from "../helper/partnerCode";

// Id du compte connecté, lu dans le jeton (lecture seule : c'est l'API qui le vérifie)
const tokenUserId = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(payload))?._id || null;
  } catch (err) {
    return null;
  }
};

const readSavedStatus = () => {
  try {
    return JSON.parse(sessionStorage.getItem("online") || "null");
  } catch (err) {
    return null;
  }
};
//import HomeTwo from "../HomeTwo";

// 👇 LA SOLUTION EST ICI : On passe les tableaux de bord en Lazy Loading !
const StudentRoutes = React.lazy(() => import("./studentRoutes"));
const TeacherRoutes = React.lazy(() => import("./teacherRoutes"));

// Lazy load components
// 🚀 Pages publiques : mêmes fonctions de chargement que le préchargement (routes/preload.js)
const Login = React.lazy(PAGE_LOADERS["/login"]);
const ForgotPassword = React.lazy(() =>
  import("../pages/account/ForgotPassword")
);
const Index = React.lazy(() => import("../pages/account"));
const Register = React.lazy(() => import("../pages/account/Register"));
const VerifyEmail = React.lazy(() => import("../pages/account/VerifyEmail"));
const Contact = React.lazy(PAGE_LOADERS["/contact"]);
const PageNotFound = React.lazy(() => import("../pages/404/PageNotFound"));
const LegalNotice = React.lazy(PAGE_LOADERS["/legalNotice"]);
const TermsCondition = React.lazy(PAGE_LOADERS["/termsCondition"]);
const PrivacyPolicy = React.lazy(PAGE_LOADERS["/privacyPolicy"]);
const HelpCenter = React.lazy(PAGE_LOADERS["/helpcenter"]);
const BecomeTeacher = React.lazy(PAGE_LOADERS["/becomeTeacher"]);
const AboutUs = React.lazy(PAGE_LOADERS["/whoAreWe"]);
const OnlineCourses = React.lazy(PAGE_LOADERS["/onlineCourses"]);
const WaitingRoom = React.lazy(() =>
  import("../pages/waitingRoom/WaitingRoom")
);
const NetworkError = React.lazy(() =>
  import("../components/common/NetworkError")
);
const MyLevel = React.lazy(PAGE_LOADERS["/MyLevel"]);
const PaymentSuccess = React.lazy(() =>
  import("../pages/Payments/PaymentSuccess")
);
const PaymentFail = React.lazy(() => import("../pages/Payments/PaymentFail"));
const Unsubscrbe = React.lazy(() => import("../components/Unsubscribe"));
// 🔁 « Ne plus recevoir ces rappels » (renouvellement des séries), lien signé
const SeriesRemindersOff = React.lazy(() => import("../components/SeriesRemindersOff"));
// 📒 Liens de l'e-mail du parent (carnet de suivi, tour 58) : confirmation, désabonnement
const CarnetParentLien = React.lazy(() => import("../components/CarnetParentLien"));
const TeacherDetails = React.lazy(PAGE_LOADERS["/Teacher-Details"]);
const SearchTeacher = React.lazy(PAGE_LOADERS["/searchTeacher"]);

const HomeTwo = React.lazy(PAGE_LOADERS["/"]);

// 🤝 Lien d'un partenaire (tour 54) : acadeclic.fr/p/CODE ouvre l'accueil, ?via=CODE ajouté à n'importe
// quelle adresse fait la même chose sur cette page. La visite est comptée (une par chargement du site),
// le code reste en mémoire (helper/partnerCode.js) et disparaît de l'adresse affichée.
export const PARTNER_LINK = /^\/p\/([^/]+)\/?$/;
export function PartnerArrival() {
  const location = useLocation();
  const history = useHistory();
  useEffect(() => {
    const link = location.pathname.match(PARTNER_LINK);
    const via = viaCodeOf(location.search);
    if (link) {
      arriveWithPartnerCode(decodeURIComponent(link[1]));
      history.replace("/");
    } else if (via) {
      arriveWithPartnerCode(via);
      history.replace({ pathname: location.pathname, search: searchWithoutVia(location.search), hash: location.hash });
    }
    // Relu seulement quand l'adresse ou ses paramètres changent (pas pour l'ancre seule)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);
  return null;
}

function MainRoutes() {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const { authUserData } = useSelector(
    ({ intialDataReducer }) => intialDataReducer
  );
  const { access, isLogin } = useSelector(
    ({ intialDataReducer }) => intialDataReducer
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const path = [
    "/dashboard",
    "/favourites",
    "/myclasses",
    "/myAccount",
    "/Subscription",
    "/updatemyadvert",
    "/myPublicProfile",
    "/myadvert",
    "/MyPayments",
    "/firstlogin",
    "/Take-A-Class",
    "/ZoomClasses",
    "/ViewStatistics",
    "/StudentStatistics",
    "/stripe-verification",
    "/myplanning",
    // Ouvertes directement (lien d'un e-mail, rechargement) : sans elles, page blanche
    "/myRequests",
    // 📒 Carnet de suivi de l'élève (tour 58)
    "/carnet",
    "/createadvert",
  ];

  useEffect(() => {
    // Statut en ligne mémorisé à la fermeture de la page (App.js, handleUnload) :
    // rétabli une seule fois, et seulement pour le compte qui l'a laissé. Après un
    // changement de compte, l'id mémorisé est celui de l'ancien compte (refus 403).
    // 🟢 Prof : jamais remis en ligne au rechargement. Page ouverte ou rechargée = reconnexion :
    // hors ligne (sauf cours en cours), puis « Vous êtes hors ligne. Passer en ligne ? ».
    const sessionData = readSavedStatus();
    sessionStorage.removeItem("online");
    const token = localStorage.getItem("token");
    if (token) {
      const isTeacher = tokenUserType(token) === 1;
      const restored =
        sessionData && sessionData.user_id === tokenUserId(token) && [0, 1, 2].includes(sessionData.status) && !(isTeacher && sessionData.status !== 2)
          ? updateStatus(sessionData)
          : null;
      if (isTeacher)
        teacher
          .startSession()
          .then((res) => res?.data?.online === 0 && requestOnlinePrompt())
          .catch(() => {})
          .finally(() => dispatch(getAuthUser(history)));
      // Élève : compte déjà lu par App.js au chargement. Relu seulement après le rétablissement
      // du statut mémorisé, une fois celui-ci enregistré (avant : relu à chaque chargement, parfois
      // avant la fin du rétablissement)
      else if (restored) restored.finally(() => dispatch(getAuthUser(history)));
    }
  }, [history]);

  // Adresse de la liste ci-dessus (« / » final et majuscules ignorés, comme les routes elles-mêmes)
  const isPrivatePath = (pathname = "") => {
    const wanted = (pathname.replace(/\/+$/, "") || "/").toLowerCase();
    return path.some((item) => item.toLowerCase() === wanted);
  };

  useEffect(() => {
    setToken(localStorage.getItem("token"));
    if (isPrivatePath(location?.pathname)) dispatch(setAccess(true));
    setLoading(false);
  }, [authUserData, location?.pathname]);

  // Update status of the Teacher
  const updateStatus = (status) => {
    return teacher
      .updateStatus(status?.user_id, status?.status)
      .then(() => {})
      .catch((error) => {
        error?.message === "Network Error" && noNetworkAction(history);
        notistack.error(t("notifications.status_update_error"));
      });
  };
  
  if (loading) {
    return <Loader />;
  }
  
  return (
    <Suspense fallback={<Loader />}>
      {/* 🔎 Titre, description et canonical de la page (helper/seo.js) */}
      <PageMeta />
      <PartnerArrival />
      <Switch>
        <Route exact path={`/`} component={HomeTwo} />
        {/* 🤝 Lien d'un partenaire : rien à afficher, PartnerArrival ouvre l'accueil */}
        <Route path={`/p/:code`} render={() => null} />
        <Route
          path={`/login`}
          render={({ match }) =>
            isLogin ? <Redirect to="/dashboard" /> : <Login match={match} />
          }
        />
        <Route path={`/forgot-password/:id`} component={ForgotPassword} />
        <Route
          path={`/registrationMain/:Pusertype`}
          render={({ match }) =>
            token ? <Redirect to="/dashboard" /> : <Index match={match} />
          }
        />
        <Route
          path={`/registration/:Ptype/:PuserType`}
          render={({ match }) =>
            token ? <Redirect to="/dashboard" /> : <Register match={match} />
          }
        />
        {/* 📧 Inscription par e-mail en attente du code : retour à la page du code */}
        <Route
          path={`/verifyEmail`}
          render={() => (token ? <Redirect to="/dashboard" /> : <VerifyEmail />)}
        />
        <Route path={`/contact`} component={Contact} />
        {/* 📰 Blog retiré (tour 35, aucun article) : ses anciennes adresses mènent à l'accueil */}
        <Route path={["/blog-grid", "/blog-details"]}>
          <Redirect to="/" />
        </Route>
        <Route path={`/legalNotice`} component={LegalNotice} />
        <Route path={`/termsCondition`} component={TermsCondition} />
        <Route path={`/privacyPolicy`} component={PrivacyPolicy} />
        <Route path={`/helpcenter`} component={HelpCenter} />
        <Route path={`/becomeTeacher`} component={BecomeTeacher} />
        <Route path={`/whoAreWe`} component={AboutUs} />
        <Route path={`/onlineCourses`} component={OnlineCourses} />
        <Route path={`/waitingRoom`} component={WaitingRoom} />
        <Route path={`/NetworkError`} component={NetworkError} />
        <Route path={`/MyLevel`} component={MyLevel} />
        <Route path={`/unSubscribe/:id/:lang`} component={Unsubscrbe} />
        <Route path={`/seriesReminders/:id/:lang`} component={SeriesRemindersOff} />
        <Route path={`/carnetParent/:action(confirm|stop|stop-bilan)/:id/:lang`} component={CarnetParentLien} />
        {/* 🔗 Adresse lisible de la fiche (tour 60) : la même route, la fiche n'est pas redessinée quand le site
            remplace l'ancienne adresse par la nouvelle */}
        <Route path={["/Teacher-Details", "/prof/:slug"]} component={TeacherDetails} />
        <Route path="/searchTeacher" component={SearchTeacher} />

        <Route path={`/paymentSuccess`} component={PaymentSuccess} />
        <Route path={`/paymentFail`} component={PaymentFail} />

        {/* LA PROTECTION DES ROUTES PRIVÉES VIENT APRÈS */}
        {access &&
          (isLogin ? (
            <>
              {authUserData?.userType === 1 ? (
                <TeacherRoutes />
              ) : (
                <StudentRoutes />
              )}
            </>
          ) : (
            <Redirect to="/login" />
          ))}
        {/* 🧭 Adresse inconnue ouverte directement (accès aux routes privées pas encore ouvert) :
            page 404, en noindex (PageMeta), au lieu d'une page blanche */}
        {!access && <Route render={({ location: { pathname } }) => (isPrivatePath(pathname) ? null : <PageNotFound />)} />}
      </Switch>
    </Suspense>
  );
}

export default MainRoutes;